import React, { useCallback, useEffect, useState } from 'react';

import Checkbox from '@ott/checkbox';
import { l10n, lang } from '@ott/l10n';
import metrics from '@ott/utility-km';

import { getBusinessTravelCheckboxValue, storageKey } from './utils/getBusinessTravelCheckboxValue';

import styles from './BusinessTravelCheckbox.scss';

const eventMap = new Map<BusinessTravelCheckboxProps['vertical'], string>();

eventMap.set('avia', 'serp_business_click');
eventMap.set('bus', 'serp_business_click');
eventMap.set('hotels', 'HOT_SERP_BUSINESS_CLICK');
eventMap.set('poezda', 'RZD_SERP_BUSINESS_CLICK');

/**
 * Отображаем чекбокс только для RU локали.
 */
const businessTravelSupported = lang.includes('ru');

export type BusinessTravelCheckboxProps = {
  vertical: 'avia' | 'poezda' | 'bus' | 'hotels';
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const BusinessTravelCheckbox = (props: BusinessTravelCheckboxProps) => {
  const { vertical } = props;

  const [value, setValue] = useState(false);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.checked;
      setValue(nextValue);

      /**
       * Аналитика.
       */
      const kmEvent = eventMap.get(vertical);

      if (kmEvent) {
        metrics(kmEvent);
      }

      /**
       * Из-за большого числа ре-рендеров компонента из-за родителей и отложенного определения
       * дефолтного значения после первого рендера случался баг, что мы переписывали значение
       * до его прочтения в useEffect, и из-за этого случалась рассинхронизация значения в браузере
       * и значения в рендере, и по этой причине чекбокс, во-первых, некорректно отображал дефолтное
       * значение, во-вторых, первые N (1-3) кликов по чекбоксу никак не меняли его состояние, хотя
       * события кликов проходили. Поэтому приходится работать с браузерным API непосредственно
       * в коллбэке, чтобы предотвратить этот баг.
       */
      if (typeof window !== 'undefined') {
        if (nextValue) {
          window.sessionStorage.setItem(storageKey, 'true');
        } else {
          window.sessionStorage.removeItem(storageKey);
        }
      }
    },
    [vertical]
  );

  useEffect(() => {
    setValue(getBusinessTravelCheckboxValue());
  }, []);

  if (!businessTravelSupported) {
    return null;
  }

  return (
    <Checkbox
      name="isBusinessTrip"
      specificLocators="is-business-trip-checkbox"
      isChecked={value}
      onChange={handleOnChange}
      labelClassName={styles.label}
      className={styles.root}
    >
      {l10n('businessTrip')}
    </Checkbox>
  );
};

export default BusinessTravelCheckbox;
